/**
 * Releases the assistant's transcript in time with its voice.
 *
 * GPT-Live sends the output transcript ahead of the audio it describes, so
 * shown as it arrives the text runs ahead of the speech. Both carry times on
 * the same server timeline (`start_ms`), and every audio frame is scheduled
 * on the AudioContext clock, so each fragment is held until the audio at its
 * `start_ms` is actually playing.
 */

interface ScheduledAudio {
  startMs: number;
  endMs: number;
  /** When this frame starts playing, on the AudioContext clock (seconds). */
  playsAt: number;
}

interface PendingFragment {
  delta: string;
  startMs: number;
  receivedAt: number;
}

export interface SpeechSyncDeps {
  /** The AudioContext clock, in seconds. */
  audioNow: () => number;
  /** Wall clock, in ms. */
  now: () => number;
  schedule: (callback: () => void, delayMs: number) => void;
  /** Called with each fragment when its audio starts. */
  release: (delta: string) => void;
}

/** Give up waiting for audio (e.g. a frame was dropped) after this long. */
const MAX_HOLD_MS = 4_000;
/** Frames kept for lookup; ~10 s at GPT-Live's 100 ms cadence. */
const MAX_FRAMES = 100;

export class SpeechSync {
  private frames: ScheduledAudio[] = [];
  private pending: PendingFragment[] = [];
  /** Scheduled but not yet shown; `drain` can take them early. */
  private inFlight = new Set<{ delta: string }>();

  constructor(private readonly deps: SpeechSyncDeps) {}

  /** An audio frame covering `[startMs, endMs)` will play at `playsAt`. */
  noteAudio(startMs: number, endMs: number, playsAt: number): void {
    this.frames.push({ startMs, endMs, playsAt });
    if (this.frames.length > MAX_FRAMES) this.frames.shift();
    this.flush();
  }

  /** A transcript fragment spoken at `startMs`. */
  addText(delta: string, startMs: number): void {
    this.pending.push({ delta, startMs, receivedAt: this.deps.now() });
    this.flush();
  }

  /** Drop everything, e.g. on hang-up. */
  reset(): void {
    this.frames = [];
    this.pending = [];
    this.inFlight.clear();
  }

  /**
   * Forget words of an interrupted answer whose audio never came: they were
   * never said. Only text timed before `beforeMs` on the session timeline;
   * from there on it is the reply, whose audio may yet play.
   */
  dropUnplayed(beforeMs: number): void {
    this.pending = this.pending.filter(
      (fragment) => fragment.startMs >= beforeMs,
    );
  }

  /**
   * Take every fragment not yet shown, now: a turn is ending and its last
   * words belong to it, not to whatever is said next.
   */
  drain(): string {
    const text =
      this.drainScheduled() +
      this.pending.map((fragment) => fragment.delta).join('');
    this.pending = [];
    return text;
  }

  /** Take only the words whose audio is already scheduled to play. */
  drainScheduled(): string {
    const text = [...this.inFlight].map((item) => item.delta).join('');
    this.inFlight.clear();
    return text;
  }

  /** When the audio at `ms` plays, on the AudioContext clock, if known. */
  private playTimeOf(ms: number): number | null {
    for (const frame of this.frames) {
      // Frames are contiguous; `ms` before the first kept frame plays now.
      if (ms < frame.startMs) return frame.playsAt;
      if (ms < frame.endMs) {
        return frame.playsAt + (ms - frame.startMs) / 1000;
      }
    }
    return null;
  }

  private flush(): void {
    const waiting: PendingFragment[] = [];
    for (const fragment of this.pending) {
      const playsAt = this.playTimeOf(fragment.startMs);
      if (playsAt !== null) {
        const delayMs = Math.max(0, (playsAt - this.deps.audioNow()) * 1000);
        const item = { delta: fragment.delta };
        this.inFlight.add(item);
        this.deps.schedule(() => {
          // Drained (or reset) since: already shown with its turn.
          if (!this.inFlight.delete(item)) return;
          this.deps.release(item.delta);
        }, delayMs);
      } else if (this.deps.now() - fragment.receivedAt >= MAX_HOLD_MS) {
        this.deps.release(fragment.delta);
      } else {
        waiting.push(fragment);
      }
    }
    this.pending = waiting;
  }
}
